import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Label,
} from '@pantry/ui';
import { useAuth } from '../../contexts/useAuth';
import { toast } from 'sonner';
import { CheckCircleIcon } from '@heroicons/react/20/solid';
import { EnvelopeIcon } from '@heroicons/react/24/outline';

const emailSchema = (t: ReturnType<typeof useLingui>['t']) =>
  z.object({
    newEmail: z.string().email(t`Please enter a valid email address`),
  });

type EmailFormData = z.infer<ReturnType<typeof emailSchema>>;

export function EmailSection() {
  const { t } = useLingui();
  const { user, updateEmail } = useAuth();
  const [isChanging, setIsChanging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EmailFormData>({
    resolver: zodResolver(emailSchema(t)),
    defaultValues: {
      newEmail: '',
    },
  });

  const onSubmit = async (data: EmailFormData) => {
    if (data.newEmail === user?.email) {
      toast.error(t`New email must be different from current email`);
      return;
    }

    setIsSubmitting(true);
    try {
      await updateEmail(data.newEmail);
      setEmailSent(true);
      toast.success(t`Confirmation email sent`);
    } catch (error) {
      const message = error instanceof Error ? error.message : t`Failed to update email`;
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    reset();
    setIsChanging(false);
    setEmailSent(false);
  };

  const handleStartChange = () => {
    setIsChanging(true);
    setEmailSent(false);
  };

  return (
    <Card className="rounded-xl" data-tour="settings-email">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="font-display flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted">
              <EnvelopeIcon className="h-4 w-4 text-muted-foreground" />
            </div>
            <Trans>Email Address</Trans>
          </CardTitle>
          <CardDescription>
            <Trans>Manage your account email address.</Trans>
          </CardDescription>
        </div>
        {!isChanging && (
          <Button variant="outline" size="sm" onClick={handleStartChange} className="rounded-xl">
            <EnvelopeIcon className="h-4 w-4 mr-2" />
            <Trans>Change Email</Trans>
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {isChanging ? (
          emailSent ? (
            // Success state - email sent
            <div className="space-y-4 animate-in fade-in slide-in-from-top-2">
              <div className="flex items-start gap-3 p-4 rounded-xl bg-fresh/10 border border-fresh/20">
                <CheckCircleIcon className="h-5 w-5 text-fresh mt-0.5 flex-shrink-0" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-fresh">
                    <Trans>Confirmation emails sent</Trans>
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <Trans>
                      We&apos;ve sent confirmation links to both your current email ({user?.email})
                      and your new email address. Please check both inboxes and click the
                      confirmation links to complete the change.
                    </Trans>
                  </p>
                </div>
              </div>
              <div className="flex justify-end">
                <Button variant="outline" onClick={handleCancel} className="rounded-xl">
                  <Trans>Done</Trans>
                </Button>
              </div>
            </div>
          ) : (
            // Form state
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {/* Current Email */}
              <div className="space-y-2">
                <Label>
                  <Trans>Current Email</Trans>
                </Label>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
              </div>

              {/* New Email */}
              <div className="space-y-2">
                <Label htmlFor="newEmail">
                  <Trans>New Email Address</Trans>
                </Label>
                <Input
                  id="newEmail"
                  type="email"
                  {...register('newEmail')}
                  placeholder={t`Enter new email address`}
                  className={`rounded-xl ${errors.newEmail ? 'border-destructive' : ''}`}
                />
                {errors.newEmail && (
                  <p className="text-sm text-destructive animate-in fade-in slide-in-from-top-1">
                    {errors.newEmail.message}
                  </p>
                )}
              </div>

              {/* Info notice */}
              <div className="rounded-xl bg-muted/50 border border-border/50 p-3 text-sm text-muted-foreground">
                <p>
                  <Trans>
                    After submitting, confirmation emails will be sent to both your current and new
                    email addresses. You must confirm from both to complete the change.
                  </Trans>
                </p>
              </div>

              {/* Action buttons */}
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                  className="rounded-xl"
                >
                  <Trans>Cancel</Trans>
                </Button>
                <Button type="submit" disabled={isSubmitting} className="rounded-xl">
                  {isSubmitting ? (
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                      <Trans>Sending...</Trans>
                    </div>
                  ) : (
                    <Trans>Send Confirmation</Trans>
                  )}
                </Button>
              </div>
            </form>
          )
        ) : (
          <div className="flex items-center gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-muted">
              <EnvelopeIcon className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">
                <Trans>Email</Trans>
              </p>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
